import * as mat from './lib/jsmatrix.js';
import { useState, useEffect, useCallback } from 'react';
import { useEventHandler } from './components.js';

function makeCamera({ pos, dir, up }) {


    dir = mat.normalize(dir);
    const right = mat.normalize(mat.cross(dir, up));
    // up = mat.cross(right, dir);

    return {
        pos, dir, right, up
    };
}

function moveCamera({ pos, dir, right, up }, move) {

    return { pos: mat.add(pos, move), dir, right, up };
};

/**
 * Moves the camera t units in the longitudinal direction.
 * Positive values move the camera forward, negative ones backward
 * @param {number} t The amount to move
 */
function moveCameraLongitudinal(cam, t) {

    const { dir } = cam;
    return moveCamera(cam, mat.scale(dir, t));
};

/**
 * Moves the camera t units in the lateral direction.
 * Positive values move the camera to the right, negative ones to the left
 * @param {number} t The amount to move
 */
function moveCameraLateral(cam, t) {
    const { right } = cam;
    return moveCamera(cam, mat.scale(right, t));

};

/**
 * Moves the camera t units in the normal direction.
 * Positive values move the camera upwards, negative ones downwards
 * @param {number} t The amount to move
 */
function moveCameraNormal(cam, t) {

    const { up } = cam;
    return moveCamera(cam, mat.scale(up, t));

};

function event2Coordinate(e, flipy) {
    flipy = mat.optional(flipy, false);
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left; //x position within the element.
    let y = e.clientY - rect.top;  //y position within the element.
    if (flipy) {
        y = rect.height - 1 - y;
    }
    return mat.VecF32.from([x, y]);
}


function useFirstPersonController({ setCam, camUp = mat.Z, canvas, speed = 0.1 }) {

    const [mouse, setMouse] = useState(
        {
            lastPos: mat.VecF32.from([0, 0]),
            currentPos: mat.VecF32.from([0, 0]),
            pressed: false
        }
    );
    const [keyMap, setKeyMap] = useState({});

    const mouseMove = useCallback(evt => {

        const mpos = event2Coordinate(evt);

        setMouse(current => {
            return {
                lastPos: current.currentPos,
                currentPos: mpos,
                pressed: current.pressed
            }
        });
    }, []);

    const mouseDown = useCallback(evt => {

        setMouse(current => {
            return {
                lastPos: current.lastPos,
                currentPos: current.currentPos,
                pressed: true
            }
        })
    }, []);
    const mouseUp = useCallback(() => {

        setMouse(current => {
            return {
                lastPos: current.lastPos,
                currentPos: current.currentPos,
                pressed: false
            }
        })
    }, []);

    const keyDown = useCallback(evt => {
        const key = evt.key;

        setKeyMap(k => {
            const newMap = Object.assign({}, k);
            newMap[key] = true;
            return newMap;
        });
    }, []);

    const keyUp = useCallback(e => {
        const key = e.key;
        setKeyMap(k => {
            const newMap = Object.assign({}, k);
            newMap[key] = false;
            return newMap;
        });
    }, []);

    useEventHandler({
        element: canvas,
        eventName: "mousedown",
        handleEvent: mouseDown
    });
    useEventHandler({
        element: canvas,
        eventName: "mouseup",
        handleEvent: mouseUp
    });
    useEventHandler({
        element: canvas,
        eventName: "mousemove",
        handleEvent: mouseMove
    });

    useEventHandler({
        element: canvas,
        eventName: "keydown",
        handleEvent: keyDown
    });
    useEventHandler({
        element: canvas,
        eventName: "keyup",
        handleEvent: keyUp
    });
    useEventHandler({
        element: canvas,
        eventName: "focusout",
        handleEvent: () => {
            setKeyMap(() => [])
        }
    });

    const updateKeys = useCallback((dt) => {
        const movement = dt * speed;
        setCam(currentCam => {
            let newCam = makeCamera(currentCam);
            let changed = false;
            if (keyMap["w"]) { // w
                newCam = moveCameraLongitudinal(newCam, movement);
                changed = true;
            }
            if (keyMap["s"]) { // s
                newCam = moveCameraLongitudinal(newCam, -movement);
                changed = true;
            }
            if (keyMap["d"]) { // d
                newCam = moveCameraLateral(newCam, movement);
                changed = true;
            }
            if (keyMap["a"]) { // a
                newCam = moveCameraLateral(newCam, -movement);
                changed = true;
            }
            if (keyMap["q"]) { // q
                newCam = moveCameraNormal(newCam, -movement);
                changed = true;
            }
            if (keyMap["e"]) { // e
                newCam = moveCameraNormal(newCam, movement);
                changed = true;
            }

            if (!changed) {
                return currentCam;
            }

            return {
                pos: newCam.pos,
                dir: newCam.dir,
                up: newCam.up
            };
        });
    }, [keyMap, setCam, speed]);

    useEffect(() => {
        updateKeys(50 / 1000);
        const interval = setInterval(() => {
            updateKeys(50 / 1000);
        }, 50);

        return () => {
            clearInterval(interval);
        }

    }, [updateKeys]);

    useEffect(() => {
        if (!mouse.pressed) {
            return;
        }

        const delta = mat.fromTo(mouse.lastPos, mouse.currentPos);
        setCam(currentCam => {
            let { theta, phi } = mat.cartesianToSpherical(currentCam.dir);

            phi -= delta.at(0) * 0.01;

            phi = phi % (2.0 * Math.PI);
            if (phi < 0) {
                phi += 2.0 * Math.PI;
            }

            theta += delta.at(1) * 0.01;
            const rad1 = mat.deg2rad(1.0);
            theta = Math.max(rad1, Math.min(Math.PI - rad1, theta));
            return {
                pos: currentCam.pos,
                dir: mat.spherical(theta, phi, 1),
                up: currentCam.up
            }
        }
        );
    }, [mouse, setCam])
}
export {
    useFirstPersonController
};
